<template>
  <div class="overflow-auto h-[70vh]">
    <v-data-table
      :headers="headers"
      :items="items"
      class="table-radius border border-[#e0e0e0]"
      disable-sort
    >
      <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
        <p :key="index" class="font-title" v-tooltip="{ text: header.text }">
          {{ header.text }}
        </p>
      </template>

      <template v-slot:[`item.employeeName`]="{ item }">
        <td class="px-4 py-3">{{ item.employeeName }}</td>
      </template>

      <template v-slot:[`item.totalPaid`]="{ item }">
        <td class="px-4 py-3">{{ item.totalPaid }}</td>
      </template>

      <template v-slot:[`item.salaryMonth`]="{ item }">
        <td class="px-4 py-3">{{ item.salaryMonth }}</td>
      </template>

      <template v-slot:[`item.salaryYear`]="{ item }">
        <td class="px-4 py-3">{{ item.salaryYear }}</td>
      </template>

      <template v-slot:[`item.paymentDate`]="{ item }">
        <td class="px-4 py-3">{{ item.paymentDate }}</td>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <td class="px-4 py-3">{{ item.status }}</td>
      </template>
    </v-data-table>

    <v-dialog v-model="uploadDialog" max-width="900px" persistent>
      <GenericBulkUpload
        v-if="uploadDialog"
        title="Upload Salary Logs"
        subtitle="Upload salary logs using Excel file"
        entityName="Salary Log"
        :requiredColumns="requiredColumns"
        uploadEndpoint="/salary-logs/bulk"
        :sampleData="sampleData"
        :showMonthMenu="true"
        :success-key="'processedSuccessfully'"
        :failed-key="'processingFailed'"
        :invalid-items-key="'failedEntries'"
        @close="closeUploadDialog"
        @uploadComplete="handleUploadComplete"
        @month-selected="handleMonthSelected"
      />
    </v-dialog>
  </div>
</template>

<script>
import GenericBulkUpload from '@/components/Workforce/CollectionManagement/GenericBulkUpload.vue'
import { calculateTaxDetails, calculateProvidentFund } from '@/utils/workforce/taxUtils'

export default {
  name: 'SalaryLogs',
  components: {
    GenericBulkUpload
  },
  data: () => ({
    uploadDialog: false,
    headers: [
      { text: 'Employee Name', value: 'employeeName' },
      { text: 'Total Paid', value: 'totalPaid' },
      { text: 'Month', value: 'salaryMonth' },
      { text: 'Year', value: 'salaryYear' },
      { text: 'Payment Date', value: 'paymentDate' },
      { text: 'Status', value: 'status' }
    ],
    items: [],
    options: {},
    requiredColumns: [
      'Employee',
      'Employee ID',
      'Total Paid',
      'Salary Month',
      'Salary Year',
      'Payment Date',
      'Income Tax',
      'Employee Provident Fund',
      'Employer Provident Fund'
    ],
    sampleData: [],
    filters: {
      month: '',
      search: ''
    }
  }),
  watch: {
    options: {
      handler() {
        this.fetchSalaryLogs()
      },
      deep: true
    }
  },
  methods: {
    async openUploadDialog() {
      await this.fetchEmployees()
      this.uploadDialog = true
    },
    closeUploadDialog() {
      this.uploadDialog = false
    },
    handleUploadComplete(response) {
      this.$toast.success('Salary logs uploaded successfully')
      this.fetchSalaryLogs()
      this.closeUploadDialog()
    },
    async fetchEmployees() {
      try {
        const currentDate = new Date()
        const currentYear = currentDate.getFullYear()

        // Fetch salary templates and user tax regimes (which now includes employee details)
        const [templatesResponse, usersResponse] = await Promise.all([
          this.$axios.get('/org/salary-templates'),
          this.$axios.get('/user/tax-regime')
        ])

        const allTemplates = templatesResponse.data.salaryTemplates || []
        // The response from /user/tax-regime contains the employee data we need
        // Handles both array and single object response
        let employees = []
        const userData = usersResponse.data.users || usersResponse.data
        if (Array.isArray(userData)) {
          employees = userData
        } else if (userData && userData._id) {
          employees = [userData]
        }

        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ]
        const currentMonth = monthNames[currentDate.getMonth()]
        const paymentDate = currentDate.toISOString().split('T')[0]

        // Process each employee
        this.sampleData = employees.map((emp) => {
          // 1. Find correct Salary Template
          const branchId = emp.branch?._id || emp.branch
          const designationId = emp.designation?._id || emp.designation
          const departmentId = emp.department?._id || emp.department

          let matchedTemplate = null

          // Specific: Branch + Desig + Dept
          matchedTemplate = allTemplates.find(
            (t) =>
              t.branch === branchId &&
              t.designation === designationId &&
              t.department === departmentId &&
              t.year == currentYear
          )

          // Branch only
          if (!matchedTemplate && branchId) {
            matchedTemplate = allTemplates.find(
              (t) =>
                t.branch === branchId &&
                (!t.designation || t.designation === 'null') &&
                (!t.department || t.department === 'null') &&
                t.year == currentYear
            )
          }

          // Global/Generic
          if (!matchedTemplate) {
            matchedTemplate = allTemplates.find(
              (t) =>
                (!t.branch || t.branch === 'null') &&
                (!t.designation || t.designation === 'null') &&
                (!t.department || t.department === 'null') &&
                t.year == currentYear
            )
          }

          // 2. Prepare Tax Data
          // Use taxRegime directly from the employee object
          const employeeTaxData = {
            taxRegime: emp.taxRegime || 'new',
            taxDeclarations: emp.taxDeclarations // Using standard deduction only
          }

          // 3. Calculate Values
          let incomeTax = 0
          let employeePF = 0
          let employerPF = 0

          if (matchedTemplate && emp.ctc) {
            const { monthlyTax } = calculateTaxDetails(emp, matchedTemplate, employeeTaxData)
            incomeTax = monthlyTax

            // Calculate Basic for PF
            const basicPercent = parseFloat(matchedTemplate.basicSalaryPercent) || 0
            const monthlyCTC = (parseFloat(emp.ctc) || 0) / 12
            const basicSalary = (monthlyCTC * basicPercent) / 100

            const pfDetails = calculateProvidentFund(basicSalary)
            employeePF = pfDetails.employeePF
            employerPF = pfDetails.employerPF
          }

          return {
            Employee: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
            'Employee ID': emp.employeeId || '',
            'Total Paid': emp.ctc ? Math.round(emp.ctc / 12) : 0,
            'Salary Month': currentMonth,
            'Salary Year': currentYear,
            'Payment Date': paymentDate,
            'Income Tax': incomeTax || 0,
            'Employee Provident Fund': employeePF || 0,
            'Employer Provident Fund': employerPF || 0
          }
        })
      } catch (error) {
        console.error('Error fetching employees for sample data:', error)
        this.$toast.error('Failed to load employee data for sample template')
      }
    },
    handleMonthSelected(monthData) {
      if (!this.sampleData.length) return

      this.sampleData = this.sampleData.map((item) => ({
        ...item,
        'Salary Month': monthData.label,
        'Salary Year': monthData.year
      }))
      console.log({ sampleData: this.sampleData })
    },
    async fetchSalaryLogs() {
      try {
        const params = {}

        if (this.filters.month) {
          params.salaryMonth = this.filters.month
        }
        if (this.filters.search) {
          params.employee = this.filters.search
        }

        const response = await this.$axios.get('/orgSetup/salary-logs', { params })
        const logs = response.data.salaryLogs || []

        this.items = logs.map((log) => {
          const emp = log.employee || {}
          return {
            ...log,
            employeeName: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown',
            paymentDate: log.paymentDate ? new Date(log.paymentDate).toLocaleDateString() : '-'
          }
        })
      } catch (error) {
        console.error('Error fetching salary logs:', error)
        this.$toast.error('Failed to fetch salary logs')
      }
    },
    applyFilters(filters) {
      this.filters = { ...this.filters, ...filters }
      this.options.page = 1
      this.fetchSalaryLogs()
    }
  },
  mounted() {
    this.fetchSalaryLogs()
  }
}
</script>

<style scoped>
.form-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
</style>
